import { createSlice } from '@reduxjs/toolkit';

/**
 * Green Cart slice — direct purchase / Stripe checkout workflow.
 *
 * DUAL-CART RULE (Design System §1.2):
 *   - SEPARATE from Blue Cart. No shared reducers, action types, or storage.
 *   - Pricing is REQUIRED here. unit_price_snapshot is locked at the time
 *     of LOCK_PRICING (just before Stripe session creation).
 *   - Status flow: draft → checkout → paid.
 */

const initialState = {
  items: [],
  // { product, quantity, configuration, unit_price_snapshot, line_total }
  status: 'draft', // 'draft' | 'checkout' | 'paid'
  stripeSessionId: null,
  subtotal: 0,
};

function recalcSubtotal(items) {
  return items.reduce((sum, i) => {
    const price = i.unit_price_snapshot ?? i.product?.base_price ?? 0;
    return sum + price * i.quantity;
  }, 0);
}

const greenCartSlice = createSlice({
  name: 'greenCart',
  initialState,
  reducers: {
    addItem: {
      reducer(state, action) {
        const { product, quantity, configuration } = action.payload;
        const existing = state.items.find((i) => i.product._id === product._id);
        if (existing) {
          existing.quantity += quantity || 1;
        } else {
          state.items.push({
            product,
            quantity: quantity || 1,
            configuration: configuration || {},
            unit_price_snapshot: product.base_price ?? 0,
          });
        }
        state.subtotal = recalcSubtotal(state.items);
      },
      prepare(product, quantity = 1, configuration = {}) {
        return { payload: { product, quantity, configuration } };
      },
    },
    removeItem(state, action) {
      state.items = state.items.filter((i) => i.product._id !== action.payload);
      state.subtotal = recalcSubtotal(state.items);
    },
    updateQuantity(state, action) {
      const { productId, quantity } = action.payload;
      const item = state.items.find((i) => i.product._id === productId);
      if (item) item.quantity = Math.max(1, quantity);
      state.subtotal = recalcSubtotal(state.items);
    },
    lockPricing(state) {
      state.items = state.items.map((i) => ({
        ...i,
        unit_price_snapshot: i.product?.base_price ?? i.unit_price_snapshot ?? 0,
        line_total: (i.product?.base_price ?? i.unit_price_snapshot ?? 0) * i.quantity,
      }));
      state.subtotal = recalcSubtotal(state.items);
      state.status = 'checkout';
    },
    setStripeSession(state, action) {
      state.stripeSessionId = action.payload;
    },
    markPaid(state) {
      state.status = 'paid';
    },
    clearCart(state) {
      state.items = [];
      state.status = 'draft';
      state.stripeSessionId = null;
      state.subtotal = 0;
    },
  },
});

export const {
  addItem: addGreenItem,
  removeItem: removeGreenItem,
  updateQuantity: updateGreenQuantity,
  lockPricing: lockGreenPricing,
  setStripeSession: setGreenStripeSession,
  markPaid: markGreenPaid,
  clearCart: clearGreenCart,
} = greenCartSlice.actions;

// ── Selectors ───────────────────────────────────────────────────────────────
export const selectGreenCart       = (s) => s.greenCart;
export const selectGreenItems      = (s) => s.greenCart.items;
export const selectGreenItemCount  = (s) =>
  s.greenCart.items.reduce((sum, i) => sum + i.quantity, 0);
export const selectGreenSubtotal   = (s) => s.greenCart.subtotal;
export const selectGreenStatus     = (s) => s.greenCart.status;

export default greenCartSlice.reducer;
